'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Menu, X, Anchor, ChevronDown, LayoutDashboard, UserCircle, LogOut, Globe, Ship, Settings, LogIn, Sun, Moon, Wrench, MessagesSquare } from 'lucide-react';
import { useAuth, useMaintenance } from '@/app/providers';
import { useAdminTheme } from '@/components/admin/AdminThemeContext';
import dynamic from 'next/dynamic';
import { t } from '@/locales';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

// Lazy-load modali auth: scaricati solo quando l'utente clicca "Accedi",
// non su ogni first-load di ogni pagina.
const LoginModal = dynamic(
  () => import('@/components/auth/LoginModal').then(m => m.LoginModal),
  { ssr: false },
);
const RegisterWizardModal = dynamic(
  () => import('@/components/auth/RegisterWizardModal'),
  { ssr: false },
);


const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  // Catalogo esperienze (overlay BIANCO sulla home): forza navbar solida → niente
  // testo bianco-su-bianco illeggibile. Settato da CatalogVariantTabs via evento.
  const [catalogOpen, setCatalogOpen] = useState(false);
  useEffect(() => {
    const onCat = (e: Event) => setCatalogOpen(!!(e as CustomEvent).detail);
    window.addEventListener('catalog:overlay', onCat as EventListener);
    return () => window.removeEventListener('catalog:overlay', onCat as EventListener);
  }, []);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const { user, isAuthenticated, logout, isAdmin, isOwnerRole, showImpersonationBanner } = useAuth();
  const { isMaintenanceMode, isDevAuthenticated, handleExitDevMode } = useMaintenance();

  // Badge "Le mie richieste": # preventivi in attesa di risposta del cliente.
  // Solo cliente (no admin/owner). Fetch al mount, non polling.
  const [quoteCount, setQuoteCount] = useState(0);
  const isCustomer = isAuthenticated && !isAdmin && !isOwnerRole;
  useEffect(() => {
    if (!isCustomer) { setQuoteCount(0); return; }
    fetch('/api/excursion-custom-requests/mine/count', { cache: 'no-store', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j?.success) setQuoteCount(j.count || 0); })
      .catch(() => undefined);
  }, [isCustomer]);
  const { mode, setMode } = useAdminTheme();

  const isAdminPage = pathname?.startsWith('/admin');
  const isVendorPage = pathname?.startsWith('/partner');
  const isDashboardPage = isAdminPage || isVendorPage;
  const isAdminDark = isDashboardPage && mode === 'dark';

  useEffect(() => {
    // La home usa un deck (premium-deck-root) con scroll DENTRO le slide
    // (overflow-y-auto), non sulla window. Lo scroll non bubbla ma è catturabile
    // in fase di CAPTURE → un solo listener su document intercetta sia lo scroll
    // window sia quello di qualsiasi container annidato (deck/catalogo).
    const handleScroll = (e?: Event) => {
      const t = e?.target as HTMLElement | null;
      const nested = t && typeof t.scrollTop === 'number' ? t.scrollTop : 0;
      setIsScrolled(window.scrollY > 10 || nested > 10);
    };
    handleScroll();
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true } as EventListenerOptions);
    };
  }, [pathname]);

  const closeMenus = () => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  // Auto-close user menu after 5 seconds
  useEffect(() => {
    if (!isUserMenuOpen) return;
    const timer = setTimeout(() => setIsUserMenuOpen(false), 5000);
    return () => clearTimeout(timer);
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    await logout();
    closeMenus();
    router.push('/');
  };


  const navLinks = [
    { name: t('navbarExt.home'), href: '/', key: 'home' },
    { name: t('navbarExt.fleet'), href: '/barche', key: 'fleet' },
    { name: t('navbarExt.destinations'), href: '/destinations', key: 'destinations' },
    { name: t('navbarExt.blog') || 'BLOG', href: '/blog', key: 'blog' },
  ];

  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    setIsDemo(window.location.hostname.startsWith('demo.'));
  }, []);

  const isHome = pathname === '/';
  // Pagine con hero scuro full-bleed (cataloghi escursioni/esperienze): l'hero
  // resta fixed sotto → navbar trasparente SEMPRE (niente solid-on-scroll).
  const isDarkHeroPage = pathname === '/escursioni-in-barca' || pathname === '/esperienze-in-barca';
  const isLight = !isMenuOpen && !catalogOpen && ((isHome && !isScrolled) || isDarkHeroPage);
  const showMaintenanceBanner = isMaintenanceMode && isDevAuthenticated;

  // ── Admin palette helpers ──────────────────────────────────────
  const navBg = isAdminDark
    ? 'bg-[rgba(13,27,48,0.85)] backdrop-blur-xl border-b border-[rgba(96,165,250,0.15)] py-4'
    : isDashboardPage
      ? 'bg-white shadow-sm py-3 border-b border-slate-200'
      : isDarkHeroPage
        // Maschera frosted: blur + tint scuro leggero → il contenuto scorre sotto
        // sfocato (non si mischia), navbar resta "trasparente" sopra l'hero scuro.
        ? 'bg-[#021a30]/30 backdrop-blur-xl border-b border-white/10 py-4'
        : !isLight
          ? 'bg-white/70 backdrop-blur-2xl shadow-[0_8px_28px_-10px_rgba(15,23,42,0.22)] py-3 border-b border-white/40'
          : 'bg-transparent py-6';

  const logoBg = isAdminDark
    ? 'bg-[rgba(96,165,250,0.15)] border border-[rgba(96,165,250,0.3)]'
    : !isLight ? 'bg-blue-600 shadow-lg shadow-blue-200' : 'bg-white';

  const logoIcon = isAdminDark ? 'text-[#60A5FA]' : !isLight ? 'text-white' : 'text-blue-600';
  const logoText = isAdminDark ? 'text-[#F4F6F8]' : !isLight ? 'text-slate-900' : 'text-white';

  const linkColor = isAdminDark ? 'text-[#9BA7B4] hover:text-[#60A5FA]' : !isLight ? 'text-slate-600' : 'text-white/90';
  const linkActive = isAdminDark ? 'text-[#60A5FA]' : 'text-blue-600';
  const linkUnderline = isAdminDark ? 'bg-[#60A5FA]' : 'bg-blue-600';

  return (
    <>
      {/* Maintenance Banner */}
      {showMaintenanceBanner && (
        <div className="bg-yellow-500 text-black px-4 py-2 flex items-center justify-between fixed top-0 left-0 right-0 z-[70] shadow-lg">
          <div className="flex items-center gap-2 font-bold text-sm">
            <Settings size={18} className="animate-spin" style={{ animationDuration: '3s' }} />
            <span className="hidden sm:inline">{t('navbar.maintenanceMode')}</span>
            <span className="sm:hidden">{t('navbar.maintenanceShort')}</span>
          </div>
          <button
            onClick={handleExitDevMode}
            className="bg-black/20 hover:bg-black/30 px-3 py-1 rounded-lg text-sm font-bold transition-all"
          >
            {t('navbar.exitMaintenance')}
          </button>
        </div>
      )}

      <nav data-cid="navbar-root" className={`fixed left-0 right-0 z-[60] transition-all duration-500 ${
        showMaintenanceBanner ? 'top-[44px]' : showImpersonationBanner ? 'top-[40px]' : isDemo ? 'top-[36px]' : 'top-0'
      } ${navBg}`}>
      <div data-cid="navbar-inner" className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">

        {/* Logo */}
        <Link
          href={isAdmin ? '/admin' : isOwnerRole ? '/partner' : '/'}
          onClick={(e) => {
            closeMenus();
            // Su home: torna alla prima sezione (#top). Deck V3 → evento deck:goto
            // (gestisce C/A/B); home legacy → scroll window.
            if (!isAdmin && !isOwnerRole && pathname === '/') {
              e.preventDefault();
              if (document.getElementById('premium-deck-root')) {
                window.dispatchEvent(new CustomEvent('deck:goto', { detail: 'top' }));
              } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }
          }}
          data-cid="navbar-logo-link"
          className="flex items-center gap-2 group cursor-pointer"
        >
          <img
            data-cid="navbar-logo-img"
            src="/logo-h.png"
            alt="Insula Charter"
            className={`h-14 w-auto object-contain transition-all duration-300 ${
              isAdminDark || isLight ? 'brightness-0 invert' : ''
            }`}
          />
          <span data-cid="navbar-logo-text" className={`text-2xl font-extrabold tracking-tight transition-colors ${logoText} sr-only`}>
            InsulaCharter
          </span>
        </Link>

        {/* Desktop Navigation Links - Hidden for admin/vendor users */}
        {!isAdmin && !isOwnerRole && (
          <div data-cid="navbar-menu" className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                onClick={closeMenus}
                data-cid={`navbar-link-${link.key}`}
                className={`text-sm font-bold uppercase tracking-wider transition-colors relative group ${linkColor} ${
                  pathname === link.href && link.key === 'home' ? linkActive : ''
                }`}
              >
                {link.name}
                {isHome && link.key === 'home' && (
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 rounded-full animate-in fade-in duration-500 ${linkUnderline}`} />
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Desktop Admin Navigation Links - Only for ADMIN users */}
        {isAdmin && (
          <div className="hidden lg:flex items-center gap-8">
            <Link
              href="/admin"
              onClick={closeMenus}
              className={`text-sm font-bold uppercase tracking-wider transition-colors relative group ${linkColor} ${
                pathname === '/admin' && !pathname.includes('/settings') ? linkActive : ''
              }`}
            >
              Dashboard
              {pathname === '/admin' && !pathname.includes('/settings') && (
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 rounded-full animate-in fade-in duration-500 ${linkUnderline}`} />
              )}
            </Link>
            <Link
              href="/admin/settings"
              onClick={closeMenus}
              className={`text-sm font-bold uppercase tracking-wider transition-colors relative group ${linkColor} ${
                pathname === '/admin/settings' ? linkActive : ''
              }`}
            >
              {t('navbarExt.settings')}
              {pathname === '/admin/settings' && (
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 rounded-full animate-in fade-in duration-500 ${linkUnderline}`} />
              )}
            </Link>
          </div>
        )}

        {/* Desktop User Actions */}
        <div data-cid="navbar-desktop-actions" className="hidden lg:flex items-center gap-4">
          {isDashboardPage && (
            <button
              onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              data-cid="navbar-theme-toggle-desktop"
              className={`p-2 rounded-xl transition-all ${
                isAdminDark
                  ? 'text-[#60A5FA] hover:bg-[rgba(96,165,250,0.12)]'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
              }`}
              title={mode === 'dark' ? t('navbarExt.lightTheme') : t('navbarExt.darkTheme')}
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}
          <LanguageSwitcher variant="desktop" dark={isAdminDark} transparent={isLight} />
          {isAuthenticated ? (
            <div data-cid="navbar-user-menu" className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                data-cid="navbar-user-menu-trigger"
                className={`flex items-center gap-3 px-5 py-2.5 rounded-full text-sm font-bold transition-all border ${
                  isAdminDark
                    ? 'bg-[rgba(255,255,255,0.06)] text-[#F4F6F8] border-[rgba(96,165,250,0.2)] hover:border-[rgba(96,165,250,0.4)] hover:bg-[rgba(255,255,255,0.1)]'
                    : isUserMenuOpen || !isLight
                      ? 'bg-white text-slate-900 border-slate-200 hover:border-blue-300 hover:shadow-md'
                      : 'bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-md'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black shadow-inner ${
                  isAdminDark
                    ? 'bg-gradient-to-tr from-[#60A5FA] to-[#93C5FD] text-[#08101A]'
                    : 'bg-gradient-to-tr from-blue-600 to-emerald-400 text-white'
                }`}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span>{user?.name || 'Account'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isUserMenuOpen && (
                <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
              )}

              {isUserMenuOpen && (
                <div data-cid="navbar-user-dropdown" className={`absolute top-full right-0 mt-3 w-72 rounded-[2rem] p-3 animate-in fade-in zoom-in-95 duration-200 z-20 ${
                  isAdminDark
                    ? 'bg-[linear-gradient(135deg,#1B3254,#132843)] border border-[rgba(96,165,250,0.2)] shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
                    : 'bg-white shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-100'
                }`}>
                  <div data-cid="navbar-user-dropdown-header" className={`p-4 border-b mb-2 ${isAdminDark ? 'border-[rgba(255,255,255,0.06)]' : 'border-slate-50'}`}>
                    <p data-cid="navbar-user-dropdown-role" className={`text-[10px] font-black uppercase tracking-widest leading-none mb-1 ${
                      isAdminDark ? 'text-[#60A5FA]' : 'text-slate-400'
                    }`}>
                      {isAdmin ? t('navbar.roleHost') : isOwnerRole ? t('navbarExt.vendorRole') : t('navbar.roleGuest')}
                    </p>
                    <p data-cid="navbar-user-dropdown-name" className={`font-bold text-lg ${isAdminDark ? 'text-[#F4F6F8]' : 'text-slate-900'}`}>{user?.name}</p>
                    <p data-cid="navbar-user-dropdown-email" className={`text-xs ${isAdminDark ? 'text-[#9BA7B4]' : 'text-slate-500'}`}>{user?.email}</p>
                  </div>
                  <div data-cid="navbar-user-dropdown-actions" className="space-y-1">
                    {!isAdmin && !isOwnerRole && (
                      <>
                        <Link
                          href="/profile"
                          onClick={closeMenus}
                          className={`flex items-center gap-3 w-full p-4 rounded-2xl transition-all font-bold group ${
                            isAdminDark
                              ? pathname === '/profile'
                                ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                                : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)] hover:text-[#F4F6F8]'
                              : pathname === '/profile' ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'
                          }`}
                        >
                          <UserCircle className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                            isAdminDark
                              ? pathname === '/profile' ? 'text-[#60A5FA]' : 'text-[#6B7A8A]'
                              : pathname === '/profile' ? 'text-blue-600' : 'text-slate-400'
                          }`} />
                          {t('navbar.myTrips')}
                        </Link>
                        <Link
                          href="/le-mie-richieste"
                          onClick={closeMenus}
                          className={`flex items-center gap-3 w-full p-4 rounded-2xl transition-all font-bold group ${
                            isAdminDark
                              ? pathname === '/le-mie-richieste' ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]' : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)] hover:text-[#F4F6F8]'
                              : pathname === '/le-mie-richieste' ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'
                          }`}
                          data-cid="navbar-my-requests"
                        >
                          <MessagesSquare className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                            isAdminDark
                              ? pathname === '/le-mie-richieste' ? 'text-[#60A5FA]' : 'text-[#6B7A8A]'
                              : pathname === '/le-mie-richieste' ? 'text-blue-600' : 'text-slate-400'
                          }`} />
                          <span className="flex-1">{t('navbar.myRequests')}</span>
                          {quoteCount > 0 && (
                            <span className="inline-grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold tabular-nums">{quoteCount}</span>
                          )}
                        </Link>
                      </>
                    )}
                    {isAdmin && (
                      <>
                        <Link
                          href="/admin"
                          onClick={closeMenus}
                          className={`flex items-center gap-3 w-full p-4 rounded-2xl transition-all font-bold group ${
                            isAdminDark
                              ? pathname === '/admin'
                                ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                                : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)] hover:text-[#F4F6F8]'
                              : pathname === '/admin' ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'
                          }`}
                        >
                          <LayoutDashboard className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                            isAdminDark
                              ? pathname === '/admin' ? 'text-[#60A5FA]' : 'text-[#6B7A8A]'
                              : pathname === '/admin' ? 'text-blue-600' : 'text-slate-400'
                          }`} />
                          {t('navbar.adminPanel')}
                        </Link>
                        <a
                          href="https://insulaservices.it/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMenus}
                          className="flex items-center gap-3 w-full p-3 rounded-xl transition-all font-semibold text-sm group hover:bg-slate-50 text-slate-500"
                        >
                          <Wrench className="w-4 h-4" />
                          {t('admin.previewServiceProvider')}
                        </a>
                      </>
                    )}
                    {isOwnerRole && !isAdmin && (
                      <Link
                        href="/partner"
                        onClick={closeMenus}
                        className={`flex items-center gap-3 w-full p-4 rounded-2xl transition-all font-bold group ${
                          pathname === '/partner' ? 'bg-blue-50 text-blue-600' : 'hover:bg-slate-50 text-slate-600'
                        }`}
                      >
                        <LayoutDashboard className={`w-5 h-5 transition-transform group-hover:scale-110 ${pathname === '/partner' ? 'text-blue-600' : 'text-slate-400'}`} />
                        {t('navbarExt.vendorPanel')}
                      </Link>
                    )}
                    <div className={`h-px my-2 mx-2 ${isAdminDark ? 'bg-[rgba(255,255,255,0.06)]' : 'bg-slate-50'}`} />
                    <button
                      onClick={handleLogout}
                      data-cid="navbar-user-logout"
                      className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold transition-all group ${
                        isAdminDark
                          ? 'text-[#E05252] hover:bg-[rgba(224,82,82,0.1)]'
                          : 'hover:bg-red-50 text-red-500'
                      }`}
                    >
                      <LogOut className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      {t('navbar.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowLoginModal(true)}
              data-cid="navbar-login-btn-desktop"
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all border ${
                isAdminDark
                  ? 'bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-[#08101A] border-transparent hover:shadow-[0_0_20px_rgba(96,165,250,0.3)]'
                  : !isLight
                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20 backdrop-blur-md'
              }`}
            >
              <LogIn className="w-4 h-4" />
              {t('navbar.login')}
            </button>
          )}
        </div>

        <div data-cid="navbar-mobile-actions" className="lg:hidden flex items-center gap-2">
          {isDashboardPage && (
            <button
              onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              data-cid="navbar-theme-toggle-mobile"
              className={`p-2 rounded-xl transition-all ${
                isAdminDark
                  ? 'text-[#60A5FA] hover:bg-[rgba(96,165,250,0.12)]'
                  : 'text-slate-500 hover:bg-slate-100'
              }`}
              title={mode === 'dark' ? t('navbarExt.lightTheme') : t('navbarExt.darkTheme')}
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-cid="navbar-mobile-toggle"
            className={`p-2.5 rounded-xl transition-all shadow-sm ${
              isAdminDark
                ? 'bg-[rgba(255,255,255,0.06)] text-[#F4F6F8] border border-[rgba(96,165,250,0.2)]'
                : !isLight ? 'bg-slate-100 text-slate-900' : 'bg-white/10 text-white backdrop-blur-md'
            }`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div data-cid="navbar-mobile-drawer" className={`lg:hidden fixed inset-x-0 ${isDemo ? 'top-[109px]' : showMaintenanceBanner ? 'top-[117px]' : 'top-[73px]'} border-t shadow-2xl transition-all duration-500 transform origin-top overflow-hidden ${
        isAdminDark
          ? 'bg-[linear-gradient(135deg,#1B3254,#132843)] border-[rgba(96,165,250,0.15)]'
          : 'bg-white border-slate-100'
      } ${
        isMenuOpen ? 'scale-y-100 opacity-100 visible' : 'scale-y-0 opacity-0 invisible'
      }`}>
        <div data-cid="navbar-mobile-drawer-inner" className="p-6 max-h-[85vh] overflow-y-auto space-y-8 pb-12">
          {/* Standard navigation - Hidden for admin/vendor users */}
          {!isAdmin && !isOwnerRole && (
            <div className="grid grid-cols-1 gap-2">
              <p className={`text-[10px] font-black uppercase tracking-widest px-4 mb-2 ${
                isAdminDark ? 'text-[#60A5FA]' : 'text-slate-400'
              }`}>{t('navbarExt.navigation')}</p>
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={closeMenus}
                  className={`flex items-center justify-between w-full p-4 rounded-2xl text-lg font-bold transition-all ${
                    isAdminDark
                      ? pathname === link.href && link.key === 'home'
                        ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                        : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                      : pathname === link.href && link.key === 'home' ? 'bg-blue-50 text-blue-600' : 'text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                  <div className={`w-2 h-2 rounded-full ${
                    isAdminDark
                      ? pathname === link.href && link.key === 'home' ? 'bg-[#60A5FA]' : 'bg-[rgba(255,255,255,0.1)]'
                      : pathname === link.href && link.key === 'home' ? 'bg-blue-600' : 'bg-slate-200'
                  }`} />
                </Link>
              ))}
            </div>
          )}

          {/* Admin navigation - Only for ADMIN users */}
          {isAdmin && (
            <div className="grid grid-cols-1 gap-2">
              <p className={`text-[10px] font-black uppercase tracking-widest px-4 mb-2 ${
                isAdminDark ? 'text-[#60A5FA]' : 'text-slate-400'
              }`}>{t('navbarExt.administration')}</p>
              <Link
                href="/admin"
                onClick={closeMenus}
                className={`flex items-center justify-between w-full p-4 rounded-2xl text-lg font-bold transition-all ${
                  isAdminDark
                    ? pathname === '/admin' && !pathname.includes('/settings')
                      ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                      : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                    : pathname === '/admin' && !pathname.includes('/settings') ? 'bg-blue-50 text-blue-600' : 'text-slate-800 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-3"><LayoutDashboard className="w-5 h-5" /> Dashboard</span>
                <div className={`w-2 h-2 rounded-full ${
                  isAdminDark
                    ? pathname === '/admin' && !pathname.includes('/settings') ? 'bg-[#60A5FA]' : 'bg-[rgba(255,255,255,0.1)]'
                    : pathname === '/admin' && !pathname.includes('/settings') ? 'bg-blue-600' : 'bg-slate-200'
                }`} />
              </Link>
              <Link
                href="/admin/settings"
                onClick={closeMenus}
                className={`flex items-center justify-between w-full p-4 rounded-2xl text-lg font-bold transition-all ${
                  isAdminDark
                    ? pathname === '/admin/settings'
                      ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                      : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                    : pathname === '/admin/settings' ? 'bg-blue-50 text-blue-600' : 'text-slate-800 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-3"><Settings className="w-5 h-5" /> {t('navbarExt.settings')}</span>
                <div className={`w-2 h-2 rounded-full ${
                  isAdminDark
                    ? pathname === '/admin/settings' ? 'bg-[#60A5FA]' : 'bg-[rgba(255,255,255,0.1)]'
                    : pathname === '/admin/settings' ? 'bg-blue-600' : 'bg-slate-200'
                }`} />
              </Link>
            </div>
          )}

          <div className={`grid grid-cols-1 gap-2 border-t pt-6 ${isAdminDark ? 'border-[rgba(255,255,255,0.06)]' : 'border-slate-50'}`}>
            <p className={`text-[10px] font-black uppercase tracking-widest px-4 mb-2 ${
              isAdminDark ? 'text-[#60A5FA]' : 'text-slate-400'
            }`}>{t('navbarExt.account')}</p>
            {isAuthenticated ? (
              <>
                <div className="px-4 py-3 mb-2">
                  <p className={`text-xs font-bold ${isAdminDark ? 'text-[#60A5FA]' : 'text-slate-400'}`}>{isAdmin ? t('navbar.roleHost') : isOwnerRole ? t('navbarExt.vendorRole') : t('navbar.roleGuest')}</p>
                  <p className={`font-bold ${isAdminDark ? 'text-[#F4F6F8]' : 'text-slate-900'}`}>{user?.name}</p>
                </div>
                {!isAdmin && !isOwnerRole && (
                  <>
                    <Link
                      href="/profile"
                      onClick={closeMenus}
                      className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${
                        isAdminDark
                          ? pathname === '/profile' ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]' : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                          : pathname === '/profile' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                      }`}
                    >
                      <UserCircle className="w-5 h-5" /> {t('navbar.myTrips')}
                    </Link>
                    <Link
                      href="/le-mie-richieste"
                      onClick={closeMenus}
                      className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${
                        isAdminDark
                          ? pathname === '/le-mie-richieste' ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]' : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                          : pathname === '/le-mie-richieste' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                      }`}
                    >
                      <MessagesSquare className="w-5 h-5" /> <span className="flex-1">{t('navbar.myRequests')}</span>
                      {quoteCount > 0 && (
                        <span className="inline-grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold tabular-nums">{quoteCount}</span>
                      )}
                    </Link>
                  </>
                )}
                {isAdmin && (
                  <>
                    <Link
                      href="/admin"
                      onClick={closeMenus}
                      className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${
                        isAdminDark
                          ? pathname === '/admin' ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]' : 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)]'
                          : pathname === '/admin' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                      }`}
                    >
                      <LayoutDashboard className="w-5 h-5" /> {t('navbar.adminPanel')}
                    </Link>
                    <a
                      href="https://insulaservices.it/dashboard"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenus}
                      className="flex items-center gap-3 w-full p-3 rounded-xl font-semibold text-sm text-slate-500"
                    >
                      <Wrench className="w-4 h-4" /> {t('admin.previewServiceProvider')}
                    </a>
                  </>
                )}
                {isOwnerRole && !isAdmin && (
                  <Link
                    href="/partner"
                    onClick={closeMenus}
                    className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${pathname === '/partner' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'}`}
                  >
                    <LayoutDashboard className="w-5 h-5" /> {t('navbarExt.vendorPanel')}
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${
                    isAdminDark ? 'text-[#E05252] hover:bg-[rgba(224,82,82,0.1)]' : 'text-red-500 hover:bg-red-50'
                  }`}
                >
                  <LogOut className="w-5 h-5" /> {t('navbar.logout')}
                </button>
              </>
            ) : (
              <button
                onClick={() => { closeMenus(); setShowLoginModal(true); }}
                className={`flex items-center gap-3 w-full p-4 rounded-2xl font-bold ${
                  isAdminDark ? 'text-[#60A5FA] hover:bg-[rgba(96,165,250,0.1)]' : 'text-blue-600 hover:bg-blue-50'
                }`}
              >
                <LogIn className="w-5 h-5" /> {t('navbar.login')}
              </button>
            )}
          </div>

          <div className="space-y-3">
            <LanguageSwitcher variant="mobile" dark={isAdminDark} />
            {!isAdmin && !isOwnerRole && (
              <Link href="/barche" onClick={closeMenus} className={`flex items-center justify-center gap-2 py-4 rounded-2xl font-bold transition-colors ${
                isAdminDark
                  ? 'bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-[#08101A] shadow-xl shadow-[rgba(96,165,250,0.2)]'
                  : 'bg-blue-600 text-white shadow-xl shadow-blue-200 hover:bg-blue-700'
              }`}>
                <Ship className="w-5 h-5" /> {t('boatDetail.bookNow')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onOpenRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }}
      />
      <RegisterWizardModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }}
      />
    </>
  );
};

export default Navbar;
